import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export interface PendingVoiceTranscript {
  itemId: string;
  previousItemId: string | null;
  role: 'user' | 'assistant';
  createdAt: string;
  persistedEventId: string | null;
}

const pendingVoiceTranscriptState = makeUseGlobalClientState<Array<PendingVoiceTranscript>>([]);

/** Add or enrich one optimistic placeholder, keyed by Realtime conversation item. */
export function upsertPendingVoiceTranscript(
  chatSessionId: string,
  transcript: Omit<PendingVoiceTranscript, 'createdAt' | 'persistedEventId'>
): void {
  const pending = pendingVoiceTranscriptState.getState(chatSessionId);
  const existingIndex = pending.findIndex(({itemId}) => itemId === transcript.itemId);
  if (existingIndex < 0) {
    pendingVoiceTranscriptState.updateState(chatSessionId, [
      ...pending,
      {...transcript, createdAt: new Date().toISOString(), persistedEventId: null},
    ]);
    return;
  }

  pendingVoiceTranscriptState.updateState(
    chatSessionId,
    pending.map((existing, index) => (index === existingIndex ? {...existing, ...transcript} : existing))
  );
}

/** Remove one optimistic placeholder by its Realtime conversation item ID. */
export function removePendingVoiceTranscript(chatSessionId: string, itemId: string): void {
  pendingVoiceTranscriptState.updateState(
    chatSessionId,
    pendingVoiceTranscriptState.getState(chatSessionId).filter((transcript) => transcript.itemId !== itemId)
  );
}

/** Keep the placeholder visible until the persisted event reaches the subscribed chat thread. */
export function markPendingVoiceTranscriptPersisted(
  chatSessionId: string,
  itemId: string,
  eventId: string
): void {
  pendingVoiceTranscriptState.updateState(
    chatSessionId,
    pendingVoiceTranscriptState
      .getState(chatSessionId)
      .map((transcript) =>
        transcript.itemId === itemId ? {...transcript, persistedEventId: eventId} : transcript
      )
  );
}

/** Remove every optimistic transcript owned by this local Voice session. */
export function clearPendingVoiceTranscripts(chatSessionId: string): void {
  pendingVoiceTranscriptState.deleteState(chatSessionId);
}

/** Subscribe the chat thread to its local Voice transcript placeholders. */
export function usePendingVoiceTranscripts(chatSessionId: string): Array<PendingVoiceTranscript> {
  const [pending] = pendingVoiceTranscriptState.useGlobalClientState(chatSessionId);
  return pending;
}

/** @internal Test-only non-reactive snapshot. */
export function getPendingVoiceTranscripts(chatSessionId: string): Array<PendingVoiceTranscript> {
  return pendingVoiceTranscriptState.getState(chatSessionId);
}
